<template>
  <div class="kline-chart" ref="chartContainer">
    <div v-if="!chartReady" class="loading">加载中...</div>
    <div v-if="chartReady" class="drawing-tools">
      <button @click="startDrawing('horizontalStraightLine')" class="drawing-btn">水平线</button>
      <button @click="startDrawing('verticalStraightLine')" class="drawing-btn">垂直线</button>
      <button @click="startDrawing('straightLine')" class="drawing-btn">直线</button>
      <button @click="startDrawing('rayLine')" class="drawing-btn">射线</button>
      <button @click="startDrawing('segment')" class="drawing-btn">线段</button>
      <button @click="cancelDrawing" class="drawing-btn">取消</button>
    </div>
  </div>
</template>

<script>
import { init, dispose, registerIndicator as register } from 'klinecharts'

export default {
  name: 'KlineChart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    currentIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      chart: null,
      chartReady: false,
      isFirstRender: true,
      candleDataCache: [],
      lastCachedIndex: -1,
      overlayIds: [],
      savedOverlays: [] // 保存的画线数据
    }
  },
  watch: {
    data: {
      handler() {
        this.isFirstRender = true
        this.updateChart()
      },
      deep: true
    },
    currentIndex() {
      this.updateChart()
    }
  },
  mounted() {
    this.registerMAIndicator()
    this.initChart()
    // 加载保存的画线数据
    this.loadSavedOverlays()
    if (this.data?.length) {
      this.updateChart()
    }
  },
  beforeDestroy() {
    // 保存画线数据
    this.saveOverlays()
    if (this.chart && this.$refs.chartContainer) {
      dispose(this.$refs.chartContainer)
    }
  },
  methods: {
    formatTime(timeStr) {
      if (!timeStr) return Date.now()

      let date
      if (typeof timeStr === 'number') {
        date = new Date(timeStr > 1e12 ? timeStr : timeStr * 1000)
      } else {
        date = new Date(timeStr)
      }

      const correctTime = date.getTime()
      return correctTime
    },

    transformKlineData(rawData) {
      if (!rawData || rawData.length === 0) return []
      return rawData.map(item => ({
        timestamp: this.formatTime(item.time),
        open: parseFloat(item.open) || 0,
        high: parseFloat(item.high) || 0,
        low: parseFloat(item.low) || 0,
        close: parseFloat(item.close) || 0,
        volume: item.volume || 0,
        hold: item.hold || 0
      }))
    },

    calculateMA(data, period) {
      if (!data || data.length === 0) return []

      const ma = []
      let sum = 0

      for (let i = 0; i < period - 1; i++) {
        sum += data[i].close
        ma.push({
          timestamp: data[i].timestamp,
          value: null
        })
      }

      for (let i = period - 1; i < data.length; i++) {
        sum += data[i].close
        const maValue = sum / period
        ma.push({
          timestamp: data[i].timestamp,
          value: maValue
        })
        sum -= data[i - period + 1].close
      }

      return ma
    },

    calculateMACD(data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) {
      if (!data || data.length < slowPeriod) return { macd: [], signal: [], histogram: [] }

      const fastEMA = this.calculateMA(data, fastPeriod)
      const slowEMA = this.calculateMA(data, slowPeriod)

      const macd = []
      for (let i = 0; i < data.length; i++) {
        if (i < slowPeriod - 1) {
          macd.push({ timestamp: data[i].timestamp, value: null })
        } else {
          const macdValue = fastEMA[i].value - slowEMA[i].value
          macd.push({ timestamp: data[i].timestamp, value: macdValue })
        }
      }

      const signal = []
      const validMacdValues = macd.filter(item => item.value !== null).map(item => item.value)
      if (validMacdValues.length >= signalPeriod) {
        const signalEMA = this.calculateEXPMA(
          validMacdValues.map((value, idx) => ({ timestamp: idx, close: value })),
          signalPeriod
        )
        const validMacd = macd.filter(item => item.value !== null)
        signalEMA.forEach((signalItem, idx) => {
          if (validMacd[idx]) {
            signal.push({
              timestamp: validMacd[idx].timestamp,
              value: signalItem.value
            })
          }
        })
      }

      const histogram = []
      for (let i = 0; i < macd.length; i++) {
        const macdValue = macd[i].value
        const signalValue = signal.find(s => s.timestamp === macd[i].timestamp)?.value || null
        if (macdValue !== null && signalValue !== null) {
          histogram.push({
            timestamp: macd[i].timestamp,
            value: macdValue - signalValue,
            color: macdValue >= signalValue ? 'rgba(239, 83, 80, 0.7)' : 'rgba(38, 166, 154, 0.7)'
          })
        } else {
          histogram.push({ timestamp: macd[i].timestamp, value: null })
        }
      }

      return { macd, signal, histogram }
    },

    initChart() {
      if (!this.$refs.chartContainer) return

      this.chart = init(this.$refs.chartContainer, {
        paneId: 'main',
        layout: [
          {
            type: 'candle',
            paneId: 'candle_pane',
            content: [
              'candle',
              {
                name: 'MA',
                calcParams: [10, 20, 60],
                figures: [
                  { key: 'ma1', title: 'MA10', type: 'line', color: '#FF6B6B' },
                  { key: 'ma2', title: 'MA20', type: 'line', color: '#4ECDC4' },
                  { key: 'ma3', title: 'MA60', type: 'line', color: '#FFD166' }
                ],
                // 当计算参数改变时，动态重建figures
                regenerateFigures: (params) => {
                  return params.map((p, i) => {
                    const colors = ['#FF6B6B', '#4ECDC4', '#FFD166'];
                    return { key: `ma${i + 1}`, title: `MA${p}`, type: 'line', color: colors[i] || '#999999' };
                  });
                }
              }
            ]
          },
          // {
          //   type: 'indicator',
          //   height: 80,
          //   content: ['VOL_HOLD']
          // },
          {
            type: 'indicator',
            height: 100,
            content: [
              {
                name: 'MACD',
                calcParams: [12, 26, 9],
                figures: [
                  { key: 'dif', title: 'DIF: ', type: 'line', color: '#FF6B6B' },
                  { key: 'dea', title: 'DEA: ', type: 'line', color: '#4ECDC4' },
                  {
                    key: 'macd',
                    title: 'MACD: ',
                    type: 'bar',
                    baseValue: 0,
                    styles: function (data, indicator, defaultStyles) {
                      var prev = data.prev, current = data.current;
                      var prevMacd = prev.indicatorData ? prev.indicatorData.macd : Number.MIN_SAFE_INTEGER;
                      var currentMacd = current.indicatorData ? current.indicatorData.macd : Number.MIN_SAFE_INTEGER;
                      var color;
                      if (currentMacd > 0) {
                        color = '#f23645'; // 上涨为红色
                      } else if (currentMacd < 0) {
                        color = '#089981'; // 下跌为绿色
                      } else {
                        color = '#888888'; // 平盘为灰色
                      }
                      var style = prevMacd < currentMacd ? 'stroke' : 'fill'; // stroke: 描边, fill: 填充
                      return { style: style, color: color, borderColor: color };
                    }
                  }
                ]
              }
            ]
          }
        ],
        // 启用画线功能
        overlay: {
          visible: true,
          enable: true
        }
      })

      this.chart.setStyles({
        grid: {
          show: true,
          horizontal: {
            show: true,
            style: 'dashed',
            size: 1,
            color: 'rgba(42, 46, 57, 0.55)',
            dashValue: [4, 4]
          },
          vertical: {
            show: true,
            style: 'dashed',
            size: 1,
            color: 'rgba(42, 46, 57, 0.55)',
            dashValue: [4, 4]
          }
        },
        candle: {
          show: true,
          type: 'candle_solid',
          bar: {
            upColor: '#f23645',
            upBorderColor: '#f23645',
            upWickColor: '#f23645',
            downColor: '#089981',
            downBorderColor: '#089981',
            downWickColor: '#089981',
            noChangeColor: '#888888',
            noChangeBorderColor: '#888888',
            noChangeWickColor: '#888888'
          },
          priceMark: {
            show: true,
            high: { show: true },
            low: { show: true },
            last: { show: true }
          },
          tooltip: { showRule: 'cross' }
        },
        xAxis: {
          show: true,
          axisLine: {
            show: true,
            color: '#2a2e39'
          },
          tickLine: {
            show: true,
            length: 5,
            color: '#2a2e39'
          },
          tickText: {
            show: true,
            color: '#787b86',
            size: 11
          }
        },
        yAxis: {
          show: true,
          axisLine: {
            show: true,
            color: '#2a2e39'
          },
          tickLine: {
            show: true,
            length: 5,
            color: '#2a2e39'
          },
          tickText: {
            show: true,
            color: '#787b86',
            size: 11
          }
        },
        crosshair: {
          show: true,
          horizontal: {
            show: true,
            line: {
              show: true,
              style: 'dashed',
              dashValue: [4, 4],
              size: 1,
              color: '#787b86'
            },
            text: {
              show: true,
              color: '#d1d4dc',
              size: 11,
              backgroundColor: '#2962ff'
            }
          },
          vertical: {
            show: true,
            line: {
              show: true,
              style: 'dashed',
              dashValue: [4, 4],
              size: 1,
              color: '#787b86'
            },
            text: {
              color: '#d1d4dc',
              size: 11,
              backgroundColor: '#2962ff'
            }
          }
        }
      })

      // MA指标已在layout中配置

      this.chartReady = true
    },

    registerMAIndicator() {
      register({
        name: 'VOL_HOLD',
        shortName: 'VOL&HOLD',
        calcParams: [],
        shouldOhlc: true,
        figures: [
          {
            key: 'vol',
            title: '成交量：',
            type: 'bar',
            baseValue: 0,
            styles: function (data, indicator, defaultStyles) {
              var kLineData = data.current.kLineData;
              var color;
              if (kLineData && kLineData.close > kLineData.open) {
                color = '#f23645'; // 上涨为红色
              } else if (kLineData && kLineData.close < kLineData.open) {
                color = '#089981'; // 下跌为绿色
              } else {
                color = '#888888'; // 平盘为灰色
              }
              return { color: color };
            }
          },
          {
            key: 'hold',
            title: '持仓：',
            type: 'line',
            color: '#FFD600',
            size: 1
          }
        ],
        calc: (dataList) => {
          return dataList.map(item => ({
            timestamp: item.timestamp,
            vol: item.volume || 0,
            hold: item.hold || 0
          }))
        }
      })
    },

    updateChart() {
      if (!this.chartReady || !this.data || this.data.length === 0) return

      try {
        const currentIndex = this.currentIndex || 0
        if (currentIndex >= this.data.length) return

        // 首次渲染或数据完全改变时使用applyNewData
        if (this.isFirstRender || this.candleDataCache.length === 0) {
          const displayData = this.transformKlineData(this.data.slice(0, currentIndex + 1))
          this.chart.applyNewData(displayData)
          setTimeout(() => {
            if (this.chart) {
              try {
                const totalBars = displayData.length
                if (totalBars > 0) {
                  const visibleBarCount = 50
                  const newTo = Math.min(totalBars - 1, currentIndex)
                  const newFrom = Math.max(0, newTo - visibleBarCount + 1)
                  this.chart.setVisibleRange({ from: newFrom, to: newTo })
                }
              } catch (e) {}
            }
          }, 0)
          this.isFirstRender = false
          this.candleDataCache = displayData
          this.lastCachedIndex = currentIndex
        } else {
          // 非首次渲染时
          if (currentIndex > this.lastCachedIndex) {
            // 向前移动，只添加新的数据
            const newData = this.data.slice(this.lastCachedIndex + 1, currentIndex + 1)
            const transformedNewData = this.transformKlineData(newData)
            transformedNewData.forEach(item => {
              this.chart.updateData(item)
            })
            this.candleDataCache = [...this.candleDataCache, ...transformedNewData]
            this.lastCachedIndex = currentIndex
          } else if (currentIndex < this.lastCachedIndex) {
            // 向后移动，重新渲染整个图表
            const displayData = this.transformKlineData(this.data.slice(0, currentIndex + 1))
            this.chart.applyNewData(displayData)
            // 直接设置可见范围，不使用setTimeout
            if (this.chart) {
              try {
                const totalBars = displayData.length
                if (totalBars > 0) {
                  const visibleBarCount = 50
                  const newTo = Math.min(totalBars - 1, currentIndex)
                  const newFrom = Math.max(0, newTo - visibleBarCount + 1)
                  this.chart.setVisibleRange({ from: newFrom, to: newTo })
                }
              } catch (e) {}
            }
            this.candleDataCache = displayData
            this.lastCachedIndex = currentIndex
          } else if (currentIndex === this.lastCachedIndex && this.candleDataCache.length === 0) {
            // 当前索引等于缓存索引但缓存为空，需要重新渲染
            const displayData = this.transformKlineData(this.data.slice(0, currentIndex + 1))
            this.chart.applyNewData(displayData)
            this.candleDataCache = displayData
          }
        }

        // MA指标已在layout中配置，不需要重复更新

        this.updateMarkers(this.data, currentIndex)
      } catch (error) {
      }
    },

    updateMarkers(rawData, currentIndex) {
      if (!this.chart) return

      this.overlayIds.forEach(id => {
        this.chart.removeOverlay(id)
      })
      this.overlayIds = []

      const visible = rawData.slice(0, currentIndex + 1)

      visible.forEach((item, idx) => {
        const timestamp = this.formatTime(item.time)

        if (item.signal === 'long' || item.signal === 'buy') {
          const overlayId = `buy-${idx}`
          this.chart.createOverlay({
            name: 'SimpleTag',
            id: overlayId,
            paneId: 'candle_pane',
            points: [{ timestamp, value: item.low }],
            styles: {
              offset: [0, 10],
              position: 'bottom',
              backgroundColor: '#089981',
              color: '#ffffff',
              borderRadius: 4,
              padding: [2, 4],
              fontSize: 12,
              borderWidth: 1,
              borderColor: '#089981'
            },
            data: { text: '买入' }
          })
          this.overlayIds.push(overlayId)
        }

        if (item.signal === 'short' || item.signal === 'sell') {
          const overlayId = `sell-${idx}`
          this.chart.createOverlay({
            name: 'SimpleTag',
            id: overlayId,
            paneId: 'candle_pane',
            points: [{ timestamp, value: item.high }],
            styles: {
              offset: [0, 10],
              position: 'top',
              backgroundColor: '#f23645',
              color: '#ffffff',
              borderRadius: 4,
              padding: [2, 4],
              fontSize: 12,
              borderWidth: 1,
              borderColor: '#f23645'
            },
            data: { text: '卖出' }
          })
          this.overlayIds.push(overlayId)
        }

        if (item.signal === 'close' || item.signal === 'close_long' || item.signal === 'close_short') {
          const overlayId = `close-${idx}`
          this.chart.createOverlay({
            name: 'SimpleTag',
            id: overlayId,
            paneId: 'candle_pane',
            points: [{ timestamp, value: item.close }],
            styles: {
              offset: [0, 0],
              position: 'center',
              backgroundColor: '#f2cc60',
              color: '#000000',
              borderRadius: 4,
              padding: [2, 4],
              fontSize: 12,
              borderWidth: 1,
              borderColor: '#f2cc60'
            },
            data: { text: '平仓' }
          })
          this.overlayIds.push(overlayId)
        }
      })
    },

    resetChart() {
      this.isFirstRender = true
      this.lastCachedIndex = -1
      this.candleDataCache = []

      this.overlayIds.forEach(id => {
        if (this.chart) this.chart.removeOverlay(id)
      })
      this.overlayIds = []

      if (this.chart) this.chart.applyNewData([])
      
      // 触发图表更新
      this.updateChart()
    },

    // 开始画线
    startDrawing(type) {
      if (this.chart) {
        this.chart.createOverlay({
          name: type,
          paneId: 'candle_pane'
        })
      }
    },

    // 取消画线
    cancelDrawing() {
      if (this.chart) {
        // 这里可以添加取消画线的逻辑
      }
    },

    // 保存画线数据
    saveOverlays() {
      if (this.chart && typeof this.chart.getOverlays === 'function') {
        try {
          const overlays = this.chart.getOverlays()
          if (overlays && overlays.length > 0) {
            localStorage.setItem('kline_overlays', JSON.stringify(overlays))
          }
        } catch (error) {
        }
      }
    },

    // 加载保存的画线数据
    loadSavedOverlays() {
      try {
        const savedOverlays = localStorage.getItem('kline_overlays')
        if (savedOverlays) {
          this.savedOverlays = JSON.parse(savedOverlays)
          // 在图表初始化后加载画线数据
          setTimeout(() => {
            if (this.chart && this.savedOverlays.length > 0) {
              this.savedOverlays.forEach(overlay => {
                try {
                  this.chart.createOverlay(overlay)
                } catch (error) {
                }
              })
            }
          }, 100)
        }
      } catch (error) {
      }
    }
  }
}
</script>

<style scoped>
.kline-chart {
  width: 100%;
  height: 100%;
  min-height: 400px;
  position: relative;
}

.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #888;
}

.drawing-tools {
  position: absolute;
  top: 10px;
  right: 90px;
  display: flex;
  gap: 8px;
  z-index: 100;
  background: rgba(18, 20, 28, 0.8);
  padding: 8px;
  border-radius: 4px;
  border: 1px solid var(--tv-border);
}

.drawing-btn {
  padding: 4px 8px;
  background: var(--tv-bg);
  border: 1px solid var(--tv-border);
  border-radius: 3px;
  color: var(--tv-text);
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s;
}

.drawing-btn:hover {
  border-color: var(--tv-primary);
  background: var(--tv-panel);
}

.drawing-btn:active {
  transform: translateY(1px);
}
</style>